import { useCallback } from "react";
import {
  useGetAllFeeConfigQuery,
  useAddFeeConfigMutation,
  useUpdateFeeConfigMutation,
  useDeleteFeeConfigMutation,
  useGetFeeConfigByIdQuery,
  useAttachCentersMutation,
  useDetachCentersMutation,
} from "../api/feeConfigApi";
import { FeeConfigPayloadType } from "../types/feeConfigRequest";
import { PaginationProps } from "../../../../core/data/interface";

export const useFeeConfig = (params?: PaginationProps) => {
  const [addFeeConfigMutation, {isLoading: isAdding}] = useAddFeeConfigMutation();
  const [updateFeeConfigMutation, {isLoading: isUpdating}] = useUpdateFeeConfigMutation();
  const [deleteFeeConfigMutation] = useDeleteFeeConfigMutation();
  const [attachCentersMutation] = useAttachCentersMutation();
  const [detachCentersMutation] = useDetachCentersMutation();

  const { data, isLoading, error } = useGetAllFeeConfigQuery({
    page: params?.page || 1,
    size: params?.size || 10,
    search: params?.search || "",
  });

  const addFeeConfig = useCallback(
    async (feeConfig: FeeConfigPayloadType) => {
      try {
        let result;
        result = await addFeeConfigMutation(feeConfig).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to add course stage fee configuration",
        };
      }
    },
    [addFeeConfigMutation]
  );

  const updateFeeConfig = useCallback(
    async (id: string, feeConfig: FeeConfigPayloadType) => {
      try {
        let result;
        result = await updateFeeConfigMutation({ id, payload: feeConfig }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to update course stage fee configuration",
        };
      }
    },
    [updateFeeConfigMutation]
  );

  const deleteFeeConfig = useCallback(
    async (id: string) => {
      try {
        const result = await deleteFeeConfigMutation({ id }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error:
            err instanceof Error
              ? err.message
              : "Failed to delete course stage fee configuration",
        };
      }
    },
    [deleteFeeConfigMutation]
  );

  const attachCenters = useCallback(
    async (id: string, centerIds: number[]) => {
      try {
        const result = await attachCentersMutation({ id, payload: { center_ids: centerIds } }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to attach centers",
        };
      }
    },
    [attachCentersMutation]
  );

  const detachCenters = useCallback(
    async (id: string, centerIds: number[]) => {
      try {
        const result = await detachCentersMutation({ id, payload: { center_ids: centerIds } }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to detach centers",
        };
      }
    },
    [detachCentersMutation]
  );

  const useGetFeeConfigById = (id: string) => {
    const { data, error, isLoading } = useGetFeeConfigByIdQuery({id}, {
      skip: !id,
    });
    
    if (error) {
      console.error('Error fetching course stage fee config by ID:', error);
    }
    return { feeConfig: data?.data || null, loading: isLoading, error };
  };
  

  return {
    feeConfigs: data?.data?.items || [],
    loading: isLoading,
    error,
    isAdding,
    isUpdating,
    addFeeConfig,
    updateFeeConfig,
    deleteFeeConfig,
    attachCenters,
    detachCenters,
    useGetFeeConfigById,
    paginationData: {
      pageSize: data?.data?.pageSize || 10,
      currentPage: data?.data?.currentPage || 1,
      totalItems: data?.data?.totalItems || 0,
      totalPages: data?.data?.totalPages || 0,
    },
  };
};
